/* Range notation: "AA,AKs-ATs,[50]KQo[/50]". A chart is written from three such strings,
   one per action, and read back into them. */

import {
  FLAT,
  LABEL,
  ORDER,
  RANKS,
  RI,
  WRITTEN,
  combosOf,
  r1,
  r3,
  type Action,
  type WrittenAction,
} from "./hands";
import type { ActionStat, Cell, Cells, Charted, Presence } from "./spot";

/** The three notation strings a chart is written from. */
export type Notes = Record<WrittenAction, string>;
export const blankNotes = (): Notes => ({ allin: "", raise: "", call: "" });

/* ---- notation, rebuilt from the weights ---- */

/** Lines a run can follow: the pairs, then each high card's suited and offsuit kickers. */
const FAMS: readonly (readonly string[])[] = (() => {
  const f: string[][] = [RANKS.map((r) => r + r)];
  for (const suf of ["s", "o"])
    RANKS.forEach((hi, i) => f.push(RANKS.slice(i + 1).map((lo) => hi + lo + suf)));
  return f;
})();

/** Any weight lookup as notation: runs of equal weight along the lines above, as ranges. */
export function notationFrom(weightOf: (hand: string) => number): string {
  const weight = (h: string | undefined): number => (h ? weightOf(h) : 0);
  const parts: string[] = [];
  for (const fam of FAMS) {
    let i = 0;
    while (i < fam.length) {
      const w = weight(fam[i]);
      if (!w) {
        i++;
        continue;
      }
      let j = i;
      while (j + 1 < fam.length && weight(fam[j + 1]) === w) j++;
      const seg = i === j ? fam[i] : `${fam[i]}-${fam[j]}`;
      const p = Math.round(w * 1000) / 10;
      parts.push(p === 100 ? `${seg}` : `[${p}]${seg}[/${p}]`);
      i = j + 1;
    }
  }
  return parts.join(",");
}

/** What one action of a chart is written as. */
export function notation(spot: { cells: Cells }, action: Action): string {
  return notationFrom((h) => spot.cells[h]?.[action] ?? 0);
}

/** The three strings a chart reads back as. */
export const notesOf = (spot: { cells: Cells }): Notes => ({
  allin: notation(spot, "allin"),
  raise: notation(spot, "raise"),
  call: notation(spot, "call"),
});

export function expandToken(tok: string): string[] {
  const one = (t: string): string => {
    const m = /^([AKQJT2-9])([AKQJT2-9])(s|o)?$/.exec(t);
    if (!m) throw new Error(`invalid hand "${t}"`);
    const [, a = "", b = "", suf] = m;
    if (a === b) {
      if (suf) throw new Error(`a pair takes no suffix: "${t}"`);
      return a + b;
    }
    if (!suf) throw new Error(`specify s or o: "${t}"`);
    return (RI[a] ?? 0) < (RI[b] ?? 0) ? a + b + suf : b + a + suf;
  };
  if (!tok.includes("-")) return [one(tok)];
  const [rawA = "", rawB] = tok.split("-");
  if (!rawB) throw new Error(`incomplete range "${tok}"`);
  const A = one(rawA);
  const B = one(rawB);
  const sufA = A.length === 3 ? A.charAt(2) : "";
  const sufB = B.length === 3 ? B.charAt(2) : "";
  if (sufA !== sufB) throw new Error(`range mixes hand types "${tok}"`);
  const span = (x: string, y: string): readonly string[] => {
    let [i, j] = [RI[x] ?? 0, RI[y] ?? 0];
    if (i > j) [i, j] = [j, i];
    return RANKS.slice(i, j + 1);
  };
  if (!sufA) return span(A.charAt(0), B.charAt(0)).map((r) => r + r);
  if (A.charAt(0) !== B.charAt(0))
    throw new Error(`range with varying high card "${tok}" is not supported`);
  return span(A.charAt(1), B.charAt(1)).map((r) => A.charAt(0) + r + sufA);
}

/** Weighted notation -> {hand: weight 0..1}. Throws a readable error. */
export function parseNotation(src: string | null | undefined): {
  weights: Record<string, number>;
  warn: string[];
} {
  const out: Record<string, number> = {};
  const warn: string[] = [];
  const s = (src ?? "").trim();
  let i = 0;
  const emit = (tok: string, w: number): void => {
    for (const h of expandToken(tok)) {
      if (out[h] !== undefined && out[h] !== w)
        warn.push(`${h} listed twice with different weights`);
      out[h] = w;
    }
  };
  while (i < s.length) {
    const ch = s.charAt(i);
    if (/[\s,]/.test(ch)) {
      i++;
      continue;
    }
    if (ch === "[") {
      const close = s.indexOf("]", i);
      if (close < 0) throw new Error("unclosed [ bracket");
      const num = s.slice(i + 1, close);
      if (!/^\d+(\.\d+)?$/.test(num)) throw new Error(`invalid weight "[${num}]"`);
      const w = parseFloat(num);
      if (w < 0 || w > 100) throw new Error(`weight outside 0-100: "[${num}]"`);
      const tag = `[/${num}]`;
      const end = s.indexOf(tag, close);
      if (end < 0) throw new Error(`missing "${tag}" closing tag`);
      for (const t of s.slice(close + 1, end).split(",")) if (t.trim()) emit(t.trim(), w / 100);
      i = end + tag.length;
    } else {
      let j = i;
      while (j < s.length && !/[,[]/.test(s.charAt(j))) j++;
      const t = s.slice(i, j).trim();
      if (t) emit(t, 1);
      i = j;
    }
  }
  return { weights: out, warn };
}

/* ---- presence ----
   Hero does not always hold a hand outright. After opening, a hand raised half the time is
   in hero's range half the time, and it should weigh half as much in every total. Presence
   is that weight: 1 for a hand hero always holds here, 0 for one that never reaches this
   spot.

   It lives in `spot.range`, a sparse map holding only the hands below 1, and is persisted —
   unlike `baseInRange`, it cannot be recovered from `cells`, which stores what hero DOES
   with a hand, not how often hero has it. A spot with no `range` field holds every hand in
   `baseInRange` outright. */
export function presenceOf(spot: Charted): Map<string, number> {
  const out = new Map<string, number>();
  for (const h of spot.baseInRange ?? Object.keys(spot.cells)) {
    const p = spot.range?.[h] ?? 1;
    if (p > 0) out.set(h, p);
  }
  return out;
}

/** Presence map -> the sparse form stored on a spot. Undefined when all held. */
export function rangeField(live: ReadonlyMap<string, number>): Presence | undefined {
  const out: Presence = {};
  for (const [h, p] of live) if (r3(p) < 1) out[h] = r3(p);
  return Object.keys(out).length ? out : undefined;
}

/**
 * Hands hero took one of `via` with in `src`, weighted by how often it did. Which actions
 * those are is the whole point: a chart reached by 3-betting is built from hero's raises
 * alone, not from everything hero did not fold.
 */
export function continuingRange(
  src: Charted,
  floor = 0,
  via: readonly Action[] = WRITTEN,
): Map<string, number> {
  const out = new Map<string, number>();
  for (const [h, p] of presenceOf(src)) {
    const d = src.cells[h] ?? {};
    let on = 0;
    for (const a of via) on += d[a] ?? 0;
    // Chain rule: how often hero holds it there, times how often it acts.
    const now = p * on;
    if (now > floor) out.set(h, now);
  }
  return out;
}

/** Notation string -> presence map, for a range typed in by hand. */
export function typedRange(note: string | null | undefined): {
  live: Map<string, number>;
  warn: string[];
} {
  const { weights, warn } = parseNotation(note);
  const out = new Map<string, number>();
  for (const [h, w] of Object.entries(weights)) if (w > 0) out.set(h, w);
  return { live: out, warn };
}

/* ---- writing a chart from its notes ---- */

/** The part of a spot `applyNotes` rewrites. */
export interface Editable extends Charted {
  actions: Action[];
  totalCombos: number;
  stats: Partial<Record<Action, ActionStat>>;
}

/**
 * Rebuilds a spot's cells + stats from three notation strings.
 * `priority` is the action last typed into. It is served its weight in full and the others
 * are trimmed to what is left, so a hand written into a field shows up there instead of the
 * whole chart refusing over 100%. Without it the strongest action wins: All-In, then Raise,
 * then Call.
 */
export function applyNotes(
  spot: Editable,
  notes: Partial<Notes>,
  priority?: string | null,
): string[] {
  const per: Partial<Record<WrittenAction, Record<string, number>>> = {};
  const warn: string[] = [];
  for (const a of WRITTEN) {
    const src = (notes[a] ?? "").trim();
    if (!src) continue;
    const r = parseNotation(src);
    per[a] = r.weights;
    warn.push(...r.warn.map((w) => `${LABEL[a]} : ${w}`));
  }

  // A hand named in the notation but absent from hero's range is one the range did not
  // anticipate. It joins at full presence, and is reported.
  const live = presenceOf(spot);
  const added: string[] = [];
  for (const w of Object.values(per))
    for (const h of Object.keys(w))
      if (!live.has(h)) {
        live.set(h, 1);
        added.push(h);
      }

  const first = WRITTEN.find((a) => a === priority);
  const order = first ? [first, ...WRITTEN.filter((a) => a !== first)] : WRITTEN;

  const cells: Cells = {};
  const totals: Partial<Record<Action, number>> = {};
  const trims: { h: string; a: WrittenAction; from: number; to: number }[] = [];
  let tot = 0;
  for (const h of FLAT) {
    const p = live.get(h);
    if (!p) continue;
    const n = combosOf(h) * p;
    tot += n;
    const d: Cell = {};
    let used = 0;
    for (const a of order) {
      const w = per[a]?.[h] ?? 0;
      if (w <= 0) continue;
      const room = 1 - used;
      // The same slack the total used to be checked against: notation rounds to a tenth of
      // a percent and must not read as an overflow.
      const got = w - room <= 0.0005 ? w : Math.max(room, 0);
      if (w - got > 0.0005) trims.push({ h, a, from: w, to: got });
      if (got > 0.0005) {
        d[a] = r3(got);
        used += got;
      }
    }
    const rest = r3(1 - used);
    if (rest > 0.0005) d.fold = rest;
    cells[h] = d;
    for (const a of ORDER) {
      const w = d[a];
      if (w) totals[a] = (totals[a] ?? 0) + w * n;
    }
  }
  if (!tot) throw new Error("no hands in this spot");

  spot.cells = cells;
  spot.baseInRange = FLAT.filter((h) => cells[h]);
  spot.range = rangeField(live);
  spot.totalCombos = r1(tot);
  spot.actions = ORDER.filter((a) => (totals[a] ?? 0) > 0);
  spot.stats = Object.fromEntries(
    spot.actions.map((a) => {
      const c = totals[a] ?? 0;
      return [a, { combos: r1(c), pct: r1((100 * c) / tot) }];
    }),
  );
  if (added.length > 8) warn.push(`${added.length} hands added outside the original range`);
  else if (added.length) warn.push(`hands added outside the original range: ${added.join(", ")}`);
  if (trims.length) {
    const pc = (w: number): string => (w * 100).toFixed(1).replace(/\.0$/, "");
    const shown = trims.slice(0, 6).map((t) => `${t.h} ${LABEL[t.a]} ${pc(t.from)}%→${pc(t.to)}%`);
    if (trims.length > 6) shown.push(`+${trims.length - 6} more`);
    warn.push("trimmed to fit 100%: " + shown.join(", "));
  }
  return warn;
}
